import { z } from 'zod';

import { enforceRangePairs } from '@/lib/api/filter-spec';
import { pageMetaSchema } from '@/lib/api/pagination';

import { REPORT_RANGE_ERROR } from '../constants';

/** One row = one **Customer** — the only aggregated tab in the Reports module (CONTEXT.md "Row
 *  grain"). A Customer with 21 devices is one row, not 21.
 *
 *  `email` is the ONLY nullable field on this tab; it renders `EMPTY_CELL` and groups at the end
 *  when the Email column is sorted, in both directions. The six counts are **non-nullable
 *  integers** on purpose: a Customer who owns nothing is `0` across the board, and a nullable
 *  count would let a hyphen reach a cell the AC says must read "0".
 *
 *  Two identities hold on every row and the fixtures must respect them (Postman stub, resolved
 *  against `subscribableCustomerDeviceSql`):
 *    activeSubscriptions + expiredSubscriptions === subscribableDevices
 *    subscribableDevices + nonSubscribableDevices === totalDevices
 *  Units superseded by an approved replacement are excluded from all six counts server-side
 *  (`activeCustomerDeviceSql`, ADO 102104) — nothing for the FE to do. */
export const subscriptionsReportRowSchema = z.object({
  customerId: z.string(),
  customerName: z.string(),
  phone: z.string(),
  email: z.string().nullable(),
  activeSubscriptions: z.number(),
  expiredSubscriptions: z.number(),
  subscribableDevices: z.number(),
  nonSubscribableDevices: z.number(),
  totalDevices: z.number(),
  expiringIn30Days: z.number(),
});

export const subscriptionsReportListResponseSchema = z.object({
  data: z.array(subscriptionsReportRowSchema),
  meta: pageMetaSchema,
});

/** RHF-draft validator for the filter drawer (ticket 09). Three fields in the STORY's order.
 *
 *  Both ranges are whole non-negative counts, and that rule is enforced **twice on purpose**: the
 *  inputs reject a letter, minus sign or decimal point as it is typed (the shared `numeric-input`
 *  guards, `allowDecimal: false`), and `.int().nonnegative()` here is the backstop for any path that
 *  does not go through the
 *  keyboard — a re-seeded draft, a future URL-restored filter, or a paste antd normalises oddly.
 *  Without it the AC ("only whole non-negative numbers are accepted") would hold only by virtue of
 *  the input widget, and a negative decimal could reach the wire.
 *
 *  The To >= From rule reuses the shared `enforceRangePairs` with ticket 01's `REPORT_RANGE_ERROR`:
 *  **do not hand-roll a `superRefine` here.** Seven Reports range pairs emit this identical string,
 *  and seven hand-written copies of one AC sentence is exactly where copies drift. The helper
 *  attaches each issue to the `To` path, which is what puts the message under the input the admin
 *  must fix. */
const countBound = z.number().int().nonnegative().nullable();

export const subscriptionsReportFiltersSchema = z
  .object({
    customerIds: z.array(z.string()),
    totalDevicesFrom: countBound,
    totalDevicesTo: countBound,
    expiringIn30DaysFrom: countBound,
    expiringIn30DaysTo: countBound,
  })
  .superRefine(
    enforceRangePairs(
      [
        ['totalDevicesFrom', 'totalDevicesTo'],
        ['expiringIn30DaysFrom', 'expiringIn30DaysTo'],
      ],
      REPORT_RANGE_ERROR,
    ),
  );
